import { forwardRef, ReactNode } from 'react';
import MuiLink, { LinkPropsColorOverrides, LinkProps as MuiLinkProps } from '@mui/material/Link';
import { styled } from '@mui/material/styles';
import { shouldForwardProp as sysShouldForwardProp } from '@mui/system';
import { OverridableStringUnion } from '@mui/types';

export * from '@mui/material/Link';

declare module '@mui/material/Link' {
  export interface LinkPropsColorOverrides {
    success: true;
    warning: true;
    info: true;
    internal: true;
    white: true;
    default: true;
    textSecondary: true;
  }
}

export interface LinkProps extends MuiLinkProps {
  color?: OverridableStringUnion<
    | 'primary'
    | 'secondary'
    | 'internal'
    | 'success'
    | 'error'
    | 'info'
    | 'warning'
    | 'white'
    | 'default'
    | 'textSecondary',
    LinkPropsColorOverrides
  >;
  inverted?: boolean;
  underline?: 'none' | 'hover' | 'always';
  /**
   * Stop propagation of onClick event - useful for example non-primary links in clickable table rows.
   */
  stopPropagation?: boolean;
  /**
   * Insert Icon ReactNode to be shown in front of the children.
   */
  startIcon?: ReactNode;
  /**
   * Insert Icon ReactNode to be shown behind the children.
   */
  endIcon?: ReactNode;
}

const StyledInlineDiv = styled('div')(() => ({
  display: 'inline',
  '& > *': {
    display: 'inline',
  },
}));

const StyledMuiLink = styled(MuiLink, {
  shouldForwardProp: (prop) => sysShouldForwardProp(prop) && prop !== 'inverted',
})<LinkProps>(({ inverted }) => ({
  ...(inverted && {
    textDecoration: 'none',
    borderBottom: '1px solid',
    '&:hover': {
      textDecoration: 'none',
      borderBottom: '1px solid transparent',
    },
  }),
}));

const Link = forwardRef<HTMLAnchorElement, LinkProps>((props, ref) => {
  const {
    color = 'primary',
    underline = 'hover',
    stopPropagation,
    onClick: onClickProp,
    startIcon,
    endIcon,
    children,
    ...otherProps
  } = props;
  const onClick = stopPropagation
    ? (e: Parameters<NonNullable<MuiLinkProps['onClick']>>[0]) => {
        e.stopPropagation();
        onClickProp?.(e);
      }
    : onClickProp;

  return (
    <StyledMuiLink ref={ref} {...otherProps} color={color} underline={underline} onClick={onClick}>
      {startIcon}
      <StyledInlineDiv>{children}</StyledInlineDiv>
      {endIcon}
    </StyledMuiLink>
  );
});

Link.displayName = 'Link';

export default Link;
